import React from 'react';
import { aboutData, contactData, whatsappMessages } from '../utils/mock';
import { Button } from './ui/button';
import { MessageCircle, Share2 } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

export const About = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.3, once: true });
  const [statsRef, statsVisible] = useScrollAnimation({ threshold: 0.2, once: true });

  const handleShareOnWhatsApp = () => {
    const text = `Check out Haarmony Interiors - Pune's Premier Interior Design Service! 🏠✨\n\n50+ Projects | 40+ Happy Clients | 20+ Years Experience\n\nVisit: ${contactData.website}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleGetQuote = () => {
    const phoneNumber = contactData.phone.replace(/\s+/g, '');
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessages.quote)}`, '_blank');
  };

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-white to-rose-50/30 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-rose-200/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-pink-200/20 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          ref={titleRef}
          className={`max-w-4xl mx-auto text-center mb-16 transition-all duration-1000 ${
            titleVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            {aboutData.title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            {aboutData.description}
          </p>
          
          {/* Share Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Button
              onClick={handleGetQuote}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              <MessageCircle className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
              Get Free Quote on WhatsApp
            </Button>
            <Button
              onClick={handleShareOnWhatsApp}
              variant="outline"
              className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-full shadow-md transition-all duration-300 group"
            >
              <Share2 className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
              Share with Friends
            </Button>
          </div>
        </div>

        {/* Stats Grid with stagger animation */}
        <div
          ref={statsRef}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 max-w-5xl mx-auto"
        >
          {aboutData.highlights.map((stat, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 group cursor-pointer ${
                statsVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ 
                transitionDelay: statsVisible ? `${index * 100}ms` : '0ms',
                transform: statsVisible ? 'translateY(0)' : 'translateY(40px)'
              }}
            >
              <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                {stat.number}
              </div>
              <div className="text-sm lg:text-base text-gray-600 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
